import { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
  type GridPaginationModel,
  type GridColumnVisibilityModel,
  type GridRowId,
  type GridRowSelectionModel,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useUser } from '@/providers/UserContext';
import LoadingMask from '@/components/shared/LoadingMask';

import { useApi } from '@/providers/ApiContext';
import {
  getManifestChecks,
  deleteManifestCheck,
} from '@/actions/manifestCheck';
import AdminManifestCheckDrawer from './AdminManifestCheckDrawer';
import type { Manifest, ManifestCheck, EntityId } from '@/types';

const PAGE_SIZE = 20;
const MAX_EXPORT_RECORDS = 1000;

interface AdminManifestChecksProps {
  manifest: Manifest | null;
  global?: boolean;
  setEmpty: (empty: boolean) => void;
}

function AdminManifestChecks({ manifest, global, setEmpty }: AdminManifestChecksProps): React.JSX.Element {
  const user = useUser();
  const api = useApi();
  const [manifest_checks, setManifestChecks] = useState<ManifestCheck[] | null>(null);
  const [loading_checks, setLoadingChecks] = useState<boolean>(true);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<GridRowId>(),
  });
  const [selected, setSelected] = useState<ManifestCheck | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [revision, setRevision] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [value, setValue] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [cursors, setCursors] = useState<Record<number, string>>({});
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    prompt: false,
    reference: false,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    setRevision((prevState) => prevState + 1);
  }, [selected]);

  const fetchManifestChecks = useCallback(
    async (page: number, startKey: string | null = null): Promise<void> => {
      if (!manifest?.entity_id || !api) return;
      setLoadingChecks(true);
      try {
        const response = await getManifestChecks(
          api,
          manifest.entity_id,
          PAGE_SIZE,
          startKey || undefined,
        );
        const sortedData = response.results.sort((a: ManifestCheck, b: ManifestCheck) => {
          const aSection = a.section || '';
          const bSection = b.section || '';
          if (aSection === bSection) {
            return (a.name > b.name ? 1 : -1);
          } else {
            return (aSection > bSection ? 1 : -1);
          }
        });
        setManifestChecks(sortedData);
        setHasNextPage(response.last_key !== null);
        if (response.last_key) {
          setCursors((prev) => ({
            ...prev,
            [page + 1]: response.last_key as string,
          }));
        }
      } catch (error) {
        console.log(error);
        setManifestChecks([]);
      } finally {
        setLoadingChecks(false);
      }
    },
    [api, manifest?.entity_id],
  );

  const fetchAllManifestChecks = useCallback(async (): Promise<ManifestCheck[]> => {
    if (!manifest?.entity_id || !api) return [];
    const allChecks: ManifestCheck[] = [];
    let startKey: string | null = null;
    let recordCount = 0;

    while (recordCount < MAX_EXPORT_RECORDS) {
      try {
        const response = await getManifestChecks(
          api,
          manifest.entity_id,
          Math.min(PAGE_SIZE, MAX_EXPORT_RECORDS - recordCount),
          startKey || undefined,
        );

        if (!response.results || response.results.length === 0) {
          break;
        }

        allChecks.push(...response.results);
        recordCount += response.results.length;

        if (!response.last_key) {
          break;
        }

        startKey = response.last_key;
      } catch (error) {
        console.error('Error fetching manifest checks for export:', error);
        break;
      }
    }

    return allChecks.sort((a, b) => {
      const aSection = a.section || '';
      const bSection = b.section || '';
      if (aSection === bSection) {
        return (a.name > b.name ? 1 : -1);
      } else {
        return (aSection > bSection ? 1 : -1);
      }
    });
  }, [api, manifest?.entity_id]);

  useEffect(() => {
    setEmpty(!manifest_checks || !manifest_checks?.length);
  }, [manifest_checks, setEmpty]);

  useEffect(() => {
    if (user?.entity_id && !manifest_checks && manifest?.entity_id) {
      setCurrentPage(0);
      setCursors({});
      fetchManifestChecks(0);
    }
  }, [user?.entity_id, manifest_checks, manifest?.entity_id, fetchManifestChecks]);

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel): void => {
      if (model.page > currentPage && hasNextPage) {
        setCurrentPage(model.page);
        const nextCursor = cursors[model.page];
        if (nextCursor) {
          fetchManifestChecks(model.page, nextCursor);
        }
      } else if (model.page < currentPage) {
        setCurrentPage(model.page);
        if (model.page === 0) {
          fetchManifestChecks(0);
        } else {
          const prevCursor = cursors[model.page];
          if (prevCursor) {
            fetchManifestChecks(model.page, prevCursor);
          }
        }
      }
    },
    [currentPage, hasNextPage, cursors, fetchManifestChecks],
  );

  const handleRowSelection = useCallback(
    (value: GridRowSelectionModel): void => {
      setRowSelectionModel(value);
      const selectedIds = value.ids ? Array.from(value.ids) : [];
      if (selectedIds.length === 1 && manifest_checks) {
        const check = manifest_checks.find(
          (check) => check.entity_id === selectedIds[0],
        );
        setSelected(check || null);
      } else {
        setSelected(null);
      }
    },
    [manifest_checks],
  );

  const handleManifestCheckUpdate = async (manifest_check: ManifestCheck): Promise<void> => {
    setManifestChecks((prevState) => {
      if (!prevState) return [manifest_check];
      return [
        ...prevState.filter(
          (check) => check.entity_id !== manifest_check.entity_id,
        ),
        manifest_check,
      ].sort((a, b) => {
        const aSection = a.section || '';
        const bSection = b.section || '';
        if (aSection === bSection) {
          return (a.name > b.name ? 1 : -1);
        } else {
          return (aSection > bSection ? 1 : -1);
        }
      });
    });
    setSelected(manifest_check);
    setRevision((prevState) => prevState + 1);
  };

  const handleDeleteManifestCheck = useCallback(
    async (check_id: EntityId): Promise<void> => {
      if (!manifest?.entity_id || !api) return;
      await deleteManifestCheck(api, manifest.entity_id, check_id);
      setCurrentPage(0);
      setCursors({});
      await fetchManifestChecks(0);
    },
    [api, manifest?.entity_id, fetchManifestChecks],
  );

  const deleteSelectedManifestChecks = useCallback(
    async (selected_ids: GridRowId[]): Promise<void> => {
      await Promise.all(
        selected_ids.map(async (check) => {
          await handleDeleteManifestCheck(check as EntityId);
        }),
      );
    },
    [handleDeleteManifestCheck],
  );

  const handleDeleteConfirm = useCallback(async (): Promise<void> => {
    const ids = rowSelectionModel.ids ? Array.from(rowSelectionModel.ids) : [];
    if (!ids.length) return;
    await deleteSelectedManifestChecks(ids);
    handleRowSelection({ type: 'include', ids: new Set<GridRowId>() });
    setSelected(null);
    setDeleteDialogOpen(false);
  }, [rowSelectionModel.ids, deleteSelectedManifestChecks, handleRowSelection]);

  const renderPrompt = useCallback((params: GridRenderCellParams<ManifestCheck>): React.JSX.Element => {
    const value = params.value as string;
    return (
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
        }}
      >
        {value}
      </span>
    );
  }, []);

  const renderName = useCallback((params: GridRenderCellParams<ManifestCheck>): React.JSX.Element => {
    return (
      <div
        className='MuiDataGrid-cell'
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <Typography variant='body2'>{params.row.name}</Typography>
      </div>
    );
  }, []);

  const convertDate = useCallback(
    (value: string): Date | null => {
      return value ? new Date(value.replace(' ', 'T') + 'Z') : null;
    },
    [],
  );

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>): void => {
    const field = (event.currentTarget as HTMLElement).dataset['field'];
    if (['prompt', 'reference'].includes(field || '')) {
      const id = (event.currentTarget.parentElement as HTMLElement)?.dataset['id'];
      if (id && manifest_checks) {
        const row = manifest_checks.find((r) => r.entity_id === id);
        if (row && field) {
          setValue(row[field as keyof ManifestCheck] as string || '');
          setAnchorEl(event.currentTarget);
        }
      }
    } else {
      setValue('');
    }
  };

  const handlePopoverClose = (): void => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const columns = useMemo((): GridColDef<ManifestCheck>[] => {
    const baseColumns: GridColDef<ManifestCheck>[] = global
      ? []
      : [
          {
            field: 'section',
            headerName: 'Section',
            type: 'string' as const,
            flex: 0.5,
          },
        ];
    baseColumns.push(
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        type: 'string' as const,
        renderCell: renderName,
      },
      {
        field: 'critical',
        headerName: 'Critical',
        flex: 0.25,
        type: 'boolean' as const,
      },
      {
        field: 'prompt',
        headerName: 'Prompt',
        type: 'string' as const,
        flex: 0.5,
        renderCell: renderPrompt,
      },
      {
        field: 'reference',
        headerName: 'Reference',
        type: 'string' as const,
        flex: 0.5,
        renderCell: renderPrompt,
      },
      {
        field: 'created_at',
        headerName: 'Created',
        type: 'dateTime' as const,
        flex: 0.75,
        valueGetter: (_value: unknown, row: ManifestCheck) => convertDate(row.created_at),
      },
      {
        field: 'updated_at',
        headerName: 'Last Changed',
        type: 'dateTime' as const,
        flex: 0.75,
        valueGetter: (_value: unknown, row: ManifestCheck) => convertDate(row.updated_at),
      },
    );
    return baseColumns;
  }, [global, renderName, convertDate, renderPrompt]);

  const handleExport = useCallback(async (): Promise<void> => {
    try {
      const allChecks = await fetchAllManifestChecks();

      // Get visible columns based on the current column visibility model
      const visibleColumns = columns.filter((column) => {
        // For global manifests, don't include section column
        if (global && column.field === 'section') {
          return false;
        }

        // Check if column is hidden in the current visibility model
        if (columnVisibilityModel[column.field] === false) {
          return false;
        }

        return true;
      });

      // Prepare data for export based on visible columns
      const exportData = allChecks.map((check) => {
        const row: Record<string, string> = {};
        visibleColumns.forEach((column) => {
          switch (column.field) {
            case 'section':
              row['section'] = check.section || '';
              break;
            case 'name':
              row['name'] = check.name || '';
              break;
            case 'critical':
              row['critical'] = check.critical ? 'Yes' : 'No';
              break;
            case 'prompt':
              row['prompt'] = check.prompt || '';
              break;
            case 'reference':
              row['reference'] = check.reference || '';
              break;
            case 'created_at':
              row['created_at'] = check.created_at
                ? new Date(
                    check.created_at.replace(' ', 'T') + 'Z',
                  ).toLocaleString()
                : '';
              break;
            case 'updated_at':
              row['updated_at'] = check.updated_at
                ? new Date(
                    check.updated_at.replace(' ', 'T') + 'Z',
                  ).toLocaleString()
                : '';
              break;
            default:
              break;
          }
        });
        return row;
      });

      // Create CSV content with only visible columns
      const headers = visibleColumns.map((column) => column.headerName);
      const csvContent = [
        headers.join(','),
        ...exportData.map((row) =>
          headers
            .map((header) => {
              const field = visibleColumns.find(
                (col) => col.headerName === header,
              )?.field;
              const value = field ? (row[field] || '') : '';
              return `"${String(value).replace(/"/g, '""')}"`; // Escape quotes
            })
            .join(','),
        ),
      ].join('\r\n');

      // Create and download file
      const blob = new Blob(['\uFEFF', csvContent], {
        type: 'text/csv;charset=utf-8;',
      });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `manifest_checks_${manifest?.name || 'export'}_${
          new Date().toISOString().split('T')[0]
        }.csv`,
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting manifest checks:', error);
    }
  }, [
    fetchAllManifestChecks,
    manifest?.name,
    columns,
    global,
    columnVisibilityModel,
  ]);

  const ManifestCheckGridToolbar = useCallback((): React.JSX.Element => {
    return (
      <GridToolbarContainer>
        <Button
          sx={{ padding: '3px 9px' }}
          variant='outlined'
          onClick={() => {
            handleRowSelection({ type: 'include', ids: new Set<GridRowId>() });
            setDrawerOpen(true);
          }}
        >
          <Stack direction='row' spacing={1} alignItems='center'>
            <AddCircleOutlineOutlinedIcon />
            <Typography variant='button' display='block' gutterBottom>
              Add
            </Typography>
          </Stack>
        </Button>
        <Button
          sx={{ padding: '3px 9px' }}
          onClick={() => {
            setDrawerOpen(true);
          }}
          disabled={!rowSelectionModel.ids || rowSelectionModel.ids.size !== 1}
        >
          <Stack direction='row' spacing={1} alignItems='center'>
            <EditOutlinedIcon />
            <Typography variant='button' display='block' gutterBottom>
              Edit
            </Typography>
          </Stack>
        </Button>
        <Button
          sx={{ padding: '3px 9px' }}
          onClick={() => setDeleteDialogOpen(true)}
          disabled={!rowSelectionModel.ids || rowSelectionModel.ids.size === 0}
        >
          <Stack direction='row' spacing={1} alignItems='center'>
            <DeleteOutlinedIcon />
            <Typography variant='button' display='block' gutterBottom>
              Delete
            </Typography>
          </Stack>
        </Button>
        <Button sx={{ padding: '3px 9px' }} onClick={handleExport}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <FileDownloadOutlinedIcon />
            <Typography variant='button' display='block' gutterBottom>
              Export All
            </Typography>
          </Stack>
        </Button>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />

        <Box sx={{ flexGrow: 1 }} />
        <GridToolbarQuickFilter />
      </GridToolbarContainer>
    );
  }, [
    handleRowSelection,
    rowSelectionModel,
    deleteSelectedManifestChecks,
    handleExport,
  ]);

  if (loading_checks) {
    return <LoadingMask />;
  }

  return (
    <>
      <Box
        sx={{
          height: 'calc(100vh - 200px)',
          width: '100%',
        }}
      >
        <DataGrid
              checkboxSelection
              columnHeaderHeight={35}
              sx={(theme) => ({
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.grey[100],
                },
              })}
              getRowId={(row) => row.entity_id}
              columns={columns}
              rows={manifest_checks || []}
              onRowSelectionModelChange={handleRowSelection}
              rowSelectionModel={rowSelectionModel}
              showToolbar
              slots={{ toolbar: ManifestCheckGridToolbar }}
              slotProps={{
                cell: {
                  onMouseEnter: handlePopoverOpen,
                  onMouseLeave: handlePopoverClose,
                },
                toolbar: {
                  sx: {
                    button: {
                      color: 'black',
                    },
                    svg: {
                      color: 'black',
                    },
                  },
                  showQuickFilter: true,
                },
              }}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={setColumnVisibilityModel}
              paginationMode='server'
              rowCount={
                hasNextPage
                  ? -1
                  : currentPage * PAGE_SIZE + (manifest_checks?.length || 0)
              }
              pageSizeOptions={[PAGE_SIZE]}
              paginationModel={{
                pageSize: PAGE_SIZE,
                page: currentPage,
              }}
              onPaginationModelChange={handlePaginationModelChange}
              loading={loading_checks}
            />
            <Popover
              sx={{
                pointerEvents: 'none',
              }}
              open={open}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              onClose={handlePopoverClose}
              disableRestoreFocus
            >
              <Typography sx={{ p: 1 }}>{value}</Typography>
            </Popover>
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby='manifest-check-delete-dialog-title'
        aria-describedby='manifest-check-delete-dialog-description'
        PaperProps={{
          sx: { maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
        }}
      >
        <DialogTitle
          id='manifest-check-delete-dialog-title'
          sx={{ flexShrink: 0 }}
        >
          Delete Manifest Check{rowSelectionModel.ids?.size !== 1 ? 's' : ''}
        </DialogTitle>
        <DialogContent
          id='manifest-check-delete-dialog-description'
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Alert severity='error'>
            This action is permanent and cannot be reversed. The selected
            check(s) will be permanently deleted and cannot be recovered. Are
            you sure you want to continue?
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Typography variant='subtitle2' color='text.secondary'>
              Selected ({rowSelectionModel.ids?.size ?? 0}):
            </Typography>
            <List
              dense
              disablePadding
              sx={{
                maxHeight: 'min(40vh, 200px)',
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {(rowSelectionModel.ids
                ? Array.from(rowSelectionModel.ids)
                : []
              ).map((id) => {
                const check = manifest_checks?.find((c) => c.entity_id === id);
                const name = check?.name ?? String(id);
                return (
                  <ListItem key={id}>
                    <ListItemText
                      primary={name}
                      primaryTypographyProps={{ noWrap: true }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} color='inherit'>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color='primary'
            variant='contained'
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <AdminManifestCheckDrawer
        key={`manifest-check-${selected?.entity_id}-${revision}`}
        open={drawerOpen}
        toggleDrawer={() => {
          setDrawerOpen(false);
        }}
        manifest_check={selected}
        manifest_id={manifest?.entity_id}
        setManifestCheck={handleManifestCheckUpdate}
        global={global}
      />
    </>
  );
}

export default AdminManifestChecks;

